import React from "react";
import { ArrowRight, Zap, XCircle, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import data from "../data/retailData.json";
import { getIcon } from "../utils/iconResolver";

const { palette: P, problemSolution: ps } = data;

const RetailProblemSolution = () => {
  const nav = useNavigate();
  return (
    <section style={{ background: P.bg, padding: "clamp(60px, 8vw, 96px) clamp(20px, 4vw, 40px)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "clamp(40px, 6vw, 64px)" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.10)", border: "1px solid rgba(93,162,162,0.25)", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
            <Zap style={{ width: 14, height: 14, color: P.teal }} />
            <span style={{ color: P.teal, fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{ps.badge}</span>
          </div>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 48px)", fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: P.ink, marginBottom: 16 }}>
            {ps.headline.main} <span style={{ color: P.teal }}>{ps.headline.accent}</span>
          </h2>
          <p style={{ fontSize: "clamp(15px, 2vw, 18px)", color: P.muted, maxWidth: 600, margin: "0 auto", lineHeight: 1.7 }}>{ps.description}</p>
        </div>

        {/* Problem / Solution grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))", gap: 20, marginBottom: 20 }}>
          {/* Without */}
          <div style={{ background: P.white, border: "1px solid #fde0e0", borderRadius: 22, padding: "clamp(24px, 4vw, 40px)", boxShadow: "0 2px 20px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 42, height: 42, background: "#fef2f2", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <XCircle style={{ width: 20, height: 20, color: "#ef4444" }} />
              </div>
              <h3 style={{ fontSize: "clamp(16px, 2vw, 20px)", fontWeight: 800, color: P.ink }}>Without Krumago Retail</h3>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {ps.problems.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#fff6f6", borderRadius: 10, border: "1px solid #fde0e0" }}>
                  <XCircle style={{ width: 16, height: 16, color: "#fca5a5", flexShrink: 0 }} />
                  <span style={{ color: "#8a6060", fontSize: "clamp(13px, 1.5vw, 14px)", lineHeight: 1.5 }}>{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* With */}
          <div style={{ background: P.white, border: "1px solid rgba(93,162,162,0.22)", borderRadius: 22, padding: "clamp(24px, 4vw, 40px)", boxShadow: "0 2px 24px rgba(93,162,162,0.07)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 42, height: 42, background: "rgba(93,162,162,0.10)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CheckCircle2 style={{ width: 20, height: 20, color: P.teal }} />
              </div>
              <h3 style={{ fontSize: "clamp(16px, 2vw, 20px)", fontWeight: 800, color: P.ink }}>With Krumago Retail</h3>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {ps.solutions.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "rgba(93,162,162,0.05)", borderRadius: 10, border: "1px solid rgba(93,162,162,0.14)" }}>
                  <CheckCircle2 style={{ width: 16, height: 16, color: P.teal, flexShrink: 0 }} />
                  <span style={{ color: P.ink, fontSize: "clamp(13px, 1.5vw, 14px)", lineHeight: 1.5, fontWeight: 500 }}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefit cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 16 }}>
          {ps.benefits.map((b, i) => (
            <div key={i}
              style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 18, padding: "clamp(20px, 3vw, 28px) clamp(18px, 2.5vw, 26px)", textAlign: "center", transition: "all 0.3s", boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 20px 48px rgba(93,162,162,0.12)"; e.currentTarget.style.borderColor = "rgba(93,162,162,0.32)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.03)"; e.currentTarget.style.borderColor = P.border; }}>
              <div style={{ width: 52, height: 52, background: "rgba(93,162,162,0.09)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: P.teal }}>
                {getIcon(b.icon, { style: { width: 22, height: 22 } })}
              </div>
              <div style={{ fontWeight: 800, fontSize: "clamp(15px, 1.8vw, 17px)", color: P.ink, marginBottom: 8 }}>{b.title}</div>
              <div style={{ color: P.muted, fontSize: "clamp(13px, 1.5vw, 14px)", lineHeight: 1.65 }}>{b.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 48 }}>
          <button onClick={() => nav("/contact")}
            style={{ background: P.teal, color: "white", border: "none", padding: "clamp(12px, 2vw, 15px) clamp(28px, 4vw, 40px)", borderRadius: 12, fontSize: "clamp(13px, 1.5vw, 15px)", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, boxShadow: "0 8px 28px rgba(93,162,162,0.32)", transition: "all 0.25s" }}
            onMouseEnter={e => { e.currentTarget.style.background = P.tealDark; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 36px rgba(93,162,162,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = P.teal; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(93,162,162,0.32)"; }}>
            {ps.ctaButton} <ArrowRight style={{ width: 17, height: 17 }} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default RetailProblemSolution;